import type { SearchResponse } from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { Search, Store, X } from 'lucide-react';
import { useState } from 'react';
import { IconButton, Input } from '../../components/ui';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { api, apiUrl } from '../../lib/api';

export type PickedLead = { businessId: string; contactId: string | null; label: string };

export type LeadPickerProps = {
  value: PickedLead | null;
  /** The lead's real name once loaded (search hits for contacts/notes are titled after the contact/note). */
  name?: string | null;
  onChange: (lead: PickedLead | null) => void;
};

/** Optional lead for an entry: search by name, contact, email or phone; one row per lead; clearable. */
export function LeadPicker({ value, name, onChange }: LeadPickerProps) {
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search.trim(), 250);
  const { data, isFetching } = useQuery({
    queryKey: ['search', q],
    queryFn: ({ signal }) => api.get<SearchResponse>(apiUrl('/api/search', { q, limit: 8 }), { signal }),
    enabled: q.length >= 2 && value === null,
  });
  const results = data?.results ?? [];
  // One row per lead, preferring the lead's own hit over its contacts, deals or notes.
  const hits = results.filter((hit, index) => {
    const own = results.findIndex((other) => other.businessId === hit.businessId && other.type === 'business');
    return own === -1 ? results.findIndex((other) => other.businessId === hit.businessId) === index : own === index;
  });

  if (value) {
    return (
      <div className="flex h-8 items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 pr-1 pl-2.5 text-[13px]">
        <span className="flex min-w-0 items-center gap-1.5">
          <Store className="size-3.5 shrink-0 text-slate-400" aria-hidden />
          <span className="truncate font-medium text-slate-900">{name ?? value.label}</span>
        </span>
        <IconButton icon={X} size="sm" label="Remove lead" onClick={() => onChange(null)} />
      </div>
    );
  }
  return (
    <div>
      <Input icon={Search} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search leads by name, contact, email or phone" />
      {q.length >= 2 && (
        <ul className="mt-1 max-h-52 overflow-auto rounded-md border border-slate-200 bg-surface text-[13px]" aria-label="Matching leads">
          {hits.length === 0 ? (
            <li className="px-2.5 py-1.5 text-slate-500">{isFetching ? 'Searching…' : 'No matching lead'}</li>
          ) : (
            hits.map((hit) => (
              <li key={hit.businessId}>
                <button
                  type="button"
                  className="block w-full px-2.5 py-1.5 text-left hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none"
                  onClick={() => {
                    onChange({ businessId: hit.businessId, contactId: hit.type === 'contact' ? hit.id : null, label: hit.title });
                    setSearch('');
                  }}
                >
                  <span className="font-medium text-slate-900">{hit.title}</span>
                  {hit.subtitle && <span className="ml-1.5 text-xs text-slate-500">{hit.subtitle}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
